import { Rastgele, DENGE } from '@kurultay/kurallar'
import { durumOlustur } from './kurulum.js'
import { turuIsle } from './tur.js'
import type { SavasOlayi } from './tur.js'
import { kullanilabilirBeceriler, durusaGoreBeceriler } from './beceri.js'
import type { Beceri } from './beceri.js'
import type { KahramanKomutu } from './komut.js'
import type { Kurulum, SavasDurumu, Kahraman, Taraf } from './tipler.js'
import type { SavasKaydi } from './motor.js'

/**
 * Adım adım oynanan savaş. Her turda oyuncunun komutunu bekler;
 * karşı taraf yapay zekâ ile oynar.
 *
 * `savasiCoz` ile aynı `turuIsle` çekirdeğini kullanır — yani otomatik ve
 * etkileşimli savaş aynı kuralları uygular, determinizm korunur.
 */
export class SavasOturumu {
  readonly #durum: SavasDurumu
  readonly #rastgele: Rastgele
  readonly #oyuncuTarafi: Taraf
  readonly #tohum: number
  readonly #olaylar: SavasOlayi[]

  constructor(kurulum: Kurulum, tohum: number, oyuncuTarafi: Taraf) {
    this.#durum = durumOlustur(kurulum)
    this.#rastgele = new Rastgele(tohum)
    this.#oyuncuTarafi = oyuncuTarafi
    this.#tohum = tohum
    this.#olaylar = [{ tip: 'savas_basladi', tohum }]
  }

  get durum(): Readonly<SavasDurumu> {
    return this.#durum
  }

  get bitti(): boolean {
    return this.#durum.bitti || this.#durum.tur > DENGE.azamiTur
  }

  get oyuncuKahramani(): Kahraman {
    const k = this.#durum.birimler.find(
      (b) => b.tur === 'kahraman' && b.taraf === this.#oyuncuTarafi,
    )
    if (!k || k.tur !== 'kahraman') throw new Error('oyuncu kahramanı bulunamadı')
    return k
  }

  /**
   * Oyuncunun şu an kullanabileceği beceriler (öfkesi yetenler).
   * Kahraman düştüyse boş döner — savaş çerilerle sürebilir ama
   * oyuncunun komut verecek kimsesi kalmamıştır.
   */
  secenekler(): Beceri[] {
    const k = this.oyuncuKahramani
    if (!k.yasiyor || this.bitti) return []
    return kullanilabilirBeceriler(k.durus, k.ofke)
  }

  /**
   * Aktif duruşun TÜM becerileri — öfkesi yetmeyenler dahil.
   *
   * Arayüz bunu kullanır: beceri barındaki sayı tuşları sabit kalsın diye.
   * Yalnızca karşılanabilenler listelenseydi öfke değiştikçe sıralama kayar,
   * "2" tuşu her turda başka beceriyi çalıştırırdı.
   */
  tumBeceriler(): Beceri[] {
    const k = this.oyuncuKahramani
    if (!k.yasiyor || this.bitti) return []
    return durusaGoreBeceriler(k.durus)
  }

  karsilanabilir(beceri: Beceri): boolean {
    return beceri.ofkeMaliyeti <= this.oyuncuKahramani.ofke
  }

  /** Kahraman düştü mü? Savaş sürerken oyuncu seyirciye döner. */
  get kahramanDustu(): boolean {
    return !this.oyuncuKahramani.yasiyor
  }

  /**
   * Oyuncu komut vermezse ne olacağı.
   *
   * Boş komut listesi `turuIsle` içinde yapay zekâya düşer: karşılanabilen
   * en pahalı beceri kullanılır ve gerekirse duruş değiştirilir. Bu, hem
   * süre dolduğunda hem otomatik savaşta hem de kahraman düştüğünde
   * kullanılan davranıştır — oyun hiçbir koşulda oyuncuyu beklemez.
   */
  varsayilanTuruOyna(): SavasOlayi[] {
    return this.turuOyna([])
  }

  /**
   * Oyuncu SONRAKİ turda duruş değiştirebilir mi?
   *
   * Dikkat: `durusDegistirilebilirMi` tur içi bir sorudur ve `buTurDurusDegisti`
   * bayrağına bakar; o bayrak turun BAŞINDA sıfırlanır. Oturum API'si ise turlar
   * ARASINDA sorgulanır — bu yüzden bayrağa bakmak düğmeyi gereksiz yere kilitler.
   * Burada tek koşul kahramanın hayatta olmasıdır.
   */
  durusDegistirilebilir(): boolean {
    return this.oyuncuKahramani.yasiyor && !this.bitti
  }

  /** Bir turu oynatır ve o turda üretilen olayları döner. */
  turuOyna(komutlar: KahramanKomutu[]): SavasOlayi[] {
    if (this.bitti) throw new Error('savaş bitti, tur oynanamaz')

    const kahramanId = this.oyuncuKahramani.id
    const olaylar = turuIsle(
      this.#durum,
      { komutlar: { [kahramanId]: komutlar } },
      this.#rastgele,
    )
    this.#olaylar.push(...olaylar)

    if (this.bitti) {
      this.#olaylar.push({
        tip: 'savas_bitti',
        kazanan: this.#durum.kazanan,
        turSayisi: Math.min(this.#durum.tur, DENGE.azamiTur),
      })
    }
    return olaylar
  }

  /** O ana kadarki tüm olayları içeren kayıt. */
  kaydiAl(): SavasKaydi {
    return {
      tohum: this.#tohum,
      olaylar: [...this.#olaylar],
      kazanan: this.#durum.kazanan,
      turSayisi: Math.min(this.#durum.tur, DENGE.azamiTur),
    }
  }
}
